import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Music, ExternalLink, Trash2, Plus, ClipboardPaste, Sparkles, Loader2, RefreshCw, KeyRound, Check, X, Zap, CalendarClock, MapPin, Radar, Pencil, Save, Building2 } from "lucide-react";

const VENUE_TYPE_LABELS = { salle: "Salle", festival_specialise: "Festival spécialisé", festival_generaliste: "Festival généraliste", frontalier: "Frontalier" };
import WatchlistTourUrls from "@/components/WatchlistTourUrls";

const inputCls = "w-full bg-[#181A20] border border-[#272A34] rounded-md px-3 py-2 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

export const ArtistWatch = () => {
  const { t } = useLang();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [openId, setOpenId] = useState(null);
  const [pasteText, setPasteText] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: artists = [] } = useQuery({ queryKey: ["artistWatch"], queryFn: async () => (await api.get("/admin/artist-watch")).data });
  const { data: tmStatus } = useQuery({ queryKey: ["tmStatus"], queryFn: async () => (await api.get("/admin/settings/ticketmaster")).data });
  const [tmKey, setTmKey] = useState("");
  const [enrichBusy, setEnrichBusy] = useState(false);

  const { data: queue = [] } = useQuery({ queryKey: ["concertQueue"], queryFn: async () => (await api.get("/admin/concert-watch/pending")).data });
  const { data: revisions = [] } = useQuery({ queryKey: ["eventRevisions"], queryFn: async () => (await api.get("/admin/event-revisions")).data });
  const [editSlug, setEditSlug] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [venueForm, setVenueForm] = useState({ name: "", city: "", url: "", lat: "", lng: "", type: "salle", priority: "B", country: "CH" });

  const startEdit = (e) => {
    setEditSlug(e.slug);
    setEditForm({ title: e.title || "", city: e.city || "", venue: e.venue || "", country: e.country || "CH",
      start_date: (e.start_date || "").slice(0, 10), end_date: (e.end_date || "").slice(0, 10),
      official_url: e.official_url || "", ticket_url: e.ticket_url || "" });
  };
  const saveEdit = async (slug) => {
    try {
      await api.put(`/admin/concert-watch/${slug}`, editForm);
      qc.invalidateQueries({ queryKey: ["concertQueue"] });
      setEditSlug(null);
      toast.success("Concert corrigé (toujours en attente de validation)");
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
  };
  const applyRevision = async (rid) => {
    try { await api.post(`/admin/event-revisions/${rid}/apply`); qc.invalidateQueries({ queryKey: ["eventRevisions"] }); qc.invalidateQueries({ queryKey: ["concertQueue"] }); toast.success("Révision appliquée"); }
    catch { toast.error(t("aw_error")); }
  };
  const dismissRevision = async (rid) => {
    try { await api.post(`/admin/event-revisions/${rid}/dismiss`); qc.invalidateQueries({ queryKey: ["eventRevisions"] }); toast.success("Révision ignorée"); }
    catch { toast.error(t("aw_error")); }
  };
  const publishConcert = async (slug) => {
    try {
      await api.post(`/admin/concert-watch/${slug}/publish`);
      qc.invalidateQueries({ queryKey: ["concertQueue"] });
      toast.success("Concert publié");
    } catch { toast.error(t("aw_error")); }
  };
  const rejectConcert = async (slug) => {
    try {
      await api.post(`/admin/concert-watch/${slug}/reject`);
      qc.invalidateQueries({ queryKey: ["concertQueue"] });
      toast.success("Concert rejeté");
    } catch { toast.error(t("aw_error")); }
  };
  const [cwBusy, setCwBusy] = useState(false);
  const scanConcertWatch = async () => {
    setCwBusy(true);
    try {
      const res = await api.post("/admin/concert-watch/run");
      const d = res.data || {};
      toast.success(`Veille concerts: ${d.created || 0} ajouté(s), ${d.found || 0} trouvé(s)`);
      qc.invalidateQueries({ queryKey: ["concertQueue"] });
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
    setCwBusy(false);
  };

  const { data: watchlistArtists = [] } = useQuery({ queryKey: ["watchlistFull"], queryFn: async () => (await api.get("/admin/watchlist")).data });

  const { data: venues = [] } = useQuery({ queryKey: ["concertVenues"], queryFn: async () => (await api.get("/admin/concert-venues")).data });
  const addVenue = async () => {
    if (!venueForm.name.trim() || !venueForm.url.trim()) { toast.error("Nom et URL requis"); return; }
    if (venueForm.lat === "" || venueForm.lng === "" || isNaN(Number(venueForm.lat)) || isNaN(Number(venueForm.lng))) { toast.error("Coordonnées (lat, lng) requises"); return; }
    try {
      await api.post("/admin/concert-venues", { ...venueForm, lat: Number(venueForm.lat), lng: Number(venueForm.lng) });
      setVenueForm({ name: "", city: "", url: "", lat: "", lng: "", type: "salle", priority: "B", country: "CH" });
      qc.invalidateQueries({ queryKey: ["concertVenues"] });
      toast.success("Salle ajoutée");
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
  };
  const toggleVenue = async (v, enabled) => {
    try { await api.put(`/admin/concert-venues/${v.id}`, { enabled }); qc.invalidateQueries({ queryKey: ["concertVenues"] }); }
    catch { toast.error(t("aw_error")); }
  };
  const delVenue = async (v) => {
    if (!window.confirm(`Supprimer la salle ?\n\n${v.name}`)) return;
    try { await api.delete(`/admin/concert-venues/${v.id}`); qc.invalidateQueries({ queryKey: ["concertVenues"] }); toast.success("Salle supprimée"); }
    catch { toast.error(t("aw_error")); }
  };

  const { data: enrichStatus } = useQuery({
    queryKey: ["enrichStatus"],
    queryFn: async () => (await api.get("/admin/watchlist/enrich-status")).data,
    refetchInterval: (q) => (q?.state?.data?.running ? 2000 : false),
  });

  const enrichAll = async (force = false) => {
    setEnrichBusy(true);
    try {
      const res = await api.post(`/admin/watchlist/enrich-all${force ? "?force=true" : ""}`);
      if (res.data.started) { toast.success(t("aw_enrich_started").replace("{n}", res.data.total)); qc.invalidateQueries({ queryKey: ["enrichStatus"] }); }
      else toast.info(t("aw_enrich_running"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); } finally { setEnrichBusy(false); }
  };

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try { await api.post("/admin/artist-watch", { name }); setName(""); qc.invalidateQueries({ queryKey: ["artistWatch"] }); }
    catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
    setBusy(false);
  };
  const del = async (a) => {
    if (!window.confirm(`${t("aw_del_q")}\n\n${a.name}`)) return;
    try { await api.delete(`/admin/artist-watch/${a.id}`); qc.invalidateQueries({ queryKey: ["artistWatch"] }); }
    catch { toast.error(t("aw_error")); }
  };
  const extract = async (a) => {
    if (!pasteText.trim()) return;
    setBusy(true);
    try {
      const res = await api.post("/admin/artist-watch/extract", { name: a.name, text: pasteText });
      toast.success(`${res.data.inserted} concert(s) ajouté(s) à la veille`);
      setPasteText(""); setOpenId(null);
      qc.invalidateQueries({ queryKey: ["discoveries"] });
      qc.invalidateQueries({ queryKey: ["artistWatch"] });
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
    setBusy(false);
  };
  const scanAuto = async () => {
    setBusy(true);
    try {
      const res = await api.post("/admin/artist-watch/scan");
      if (res.data?.ok) {
        toast.success(`${res.data.inserted} concert(s) trouvé(s) via Ticketmaster`);
        qc.invalidateQueries({ queryKey: ["discoveries"] });
        qc.invalidateQueries({ queryKey: ["artistWatch"] });
      } else {
        toast.error(t("aw_nokey"));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
    setBusy(false);
  };
  const saveTmKey = async () => {
    if (!tmKey.trim()) return;
    try {
      await api.put("/admin/settings/ticketmaster", { api_key: tmKey });
      setTmKey("");
      qc.invalidateQueries({ queryKey: ["tmStatus"] });
      toast.success(t("aw_tm_saved"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("aw_error")); }
  };
  const resetTmKey = async () => {
    try { await api.put("/admin/settings/ticketmaster", { api_key: "" }); qc.invalidateQueries({ queryKey: ["tmStatus"] }); toast.success(t("aw_tm_reset_done")); }
    catch { toast.error(t("aw_error")); }
  };

  return (
    <div className="bg-[#121418] border border-indigo-800/40 rounded-lg p-5 mb-6" data-testid="artist-watch">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2">
          <Music className="w-5 h-5 text-indigo-300" />
          <h3 className="font-serif text-lg text-indigo-100">{t("aw_title")} ({artists.length})</h3>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="aw-concert-scan" onClick={scanConcertWatch} disabled={cwBusy} className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-3 py-1.5 rounded-md disabled:opacity-60">
            {cwBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Radar className="w-3.5 h-3.5" />} Scan veille concerts
          </button>
          <button data-testid="aw-auto-scan" onClick={scanAuto} disabled={busy} className="inline-flex items-center gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-3 py-1.5 rounded-md disabled:opacity-60">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} {t("aw_auto")}
          </button>
        </div>
      </div>
      <p className="text-xs text-stone-400 mb-4">{t("aw_desc")}</p>

      {/* Portée de la veille : toute la watchlist */}
      <div className="flex flex-wrap items-center gap-3 mb-4 rounded-md border border-amber-700/50 bg-amber-950/20 p-3" data-testid="aw-watchlist-scope">
        <Radar className="w-5 h-5 text-amber-400 shrink-0" />
        <div className="flex-1 min-w-[200px]">
          <div className="font-serif text-base text-amber-100" data-testid="aw-watched-count">{watchlistArtists.length} artistes surveillés</div>
          <div className="text-xs text-stone-400">La veille concerts couvre l'intégralité de la watchlist (Ticketmaster + sources officielles), pas seulement les groupes phares.</div>
        </div>
        <button data-testid="aw-scan-all" onClick={scanConcertWatch} disabled={cwBusy} className="inline-flex items-center gap-1.5 text-sm bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-4 py-2 rounded-md disabled:opacity-60">
          {cwBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Radar className="w-4 h-4" />} Scanner toute la watchlist
        </button>
      </div>

      {/* Enrichissement auto des fiches groupes */}
      <div className="flex flex-wrap items-center gap-3 mb-4 rounded-md border border-amber-800/40 bg-amber-950/10 p-2.5" data-testid="aw-enrich-panel">
        <Sparkles className="w-4 h-4 text-amber-400 shrink-0" />
        <span className="text-xs text-stone-300 flex-1 min-w-[180px]">{t("aw_enrich_desc")}</span>
        {enrichStatus?.running ? (
          <span className="text-xs font-mono text-amber-300" data-testid="aw-enrich-progress">{t("aw_enrich_progress").replace("{done}", enrichStatus.done).replace("{total}", enrichStatus.total)}</span>
        ) : enrichStatus?.total ? (
          <span className="text-xs font-mono text-stone-500" data-testid="aw-enrich-last">{t("aw_enrich_last").replace("{n}", enrichStatus.updated)}</span>
        ) : null}
        <button data-testid="aw-enrich-missing" onClick={() => enrichAll(false)} disabled={enrichBusy || enrichStatus?.running} className="inline-flex items-center gap-1.5 text-xs bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-3 py-1.5 rounded-md disabled:opacity-60">
          {(enrichBusy || enrichStatus?.running) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {t("aw_enrich_missing")}
        </button>
        <button data-testid="aw-enrich-all" onClick={() => enrichAll(true)} disabled={enrichBusy || enrichStatus?.running} className="inline-flex items-center gap-1.5 text-xs bg-[#181A20] text-stone-300 border border-[#272A34] hover:border-amber-500/40 px-3 py-1.5 rounded-md disabled:opacity-60">
          <RefreshCw className="w-3.5 h-3.5" /> {t("aw_enrich_all")}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 rounded-md border border-[#272A34] bg-[#181A20] p-2.5">
        <KeyRound className="w-4 h-4 text-indigo-300 shrink-0" />
        <span className="text-xs text-stone-400">{t("aw_tm_key")}{tmStatus?.configured ? ` · ${t("aw_tm_ok")}` : ""}</span>
        <input data-testid="aw-tm-key" type="password" value={tmKey} onChange={(e) => setTmKey(e.target.value)} placeholder={t("aw_tm_ph")} className="flex-1 min-w-[160px] bg-[#121418] border border-[#272A34] rounded-md px-3 py-1.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500" />
        <button data-testid="aw-tm-save" onClick={saveTmKey} className="text-xs bg-[#121418] border border-[#272A34] hover:border-indigo-500/40 text-stone-200 px-3 py-1.5 rounded-md">{t("aw_tm_save")}</button>
        {tmStatus?.configured && <button data-testid="aw-tm-reset" onClick={resetTmKey} className="text-xs text-red-400/70 hover:text-red-400 px-2 py-1.5">{t("aw_tm_reset")}</button>}
      </div>

      {/* Validation humaine stricte (auto-publication supprimée) */}
      <div className="flex flex-wrap items-center gap-3 mb-4 rounded-md border border-emerald-800/40 bg-emerald-950/10 p-2.5" data-testid="aw-validation-note">
        <Zap className="w-4 h-4 shrink-0 text-emerald-400" />
        <span className="text-xs text-stone-300 flex-1 min-w-[200px]">
          Validation humaine obligatoire : chaque concert détecté (Ticketmaster + sources officielles) passe en file « À valider ». Aucune publication automatique.
        </span>
      </div>

      {/* Salles suisses — canal concerts (géré par l'admin) */}
      <div className="mb-4 rounded-md border border-indigo-800/40 bg-indigo-950/10 p-3" data-testid="venue-panel">
        <div className="flex items-center gap-2 mb-2">
          <Building2 className="w-4 h-4 text-indigo-300" />
          <h4 className="font-serif text-sm text-indigo-100">Salles suisses — canal concerts ({venues.length})</h4>
        </div>
        <p className="text-[11px] text-stone-400 mb-3">Pages de salles scannées pendant la veille concerts. Seuls les concerts d'artistes de la watchlist entrent en file de validation (jamais publiés automatiquement).</p>
        <div className="space-y-2 mb-3">
          {venues.length === 0 && <div className="text-xs text-stone-500" data-testid="venue-none">Aucune salle configurée.</div>}
          {venues.map((v) => (
            <div key={v.id} data-testid={`venue-${v.id}`} className="flex items-center gap-3 p-2.5 rounded-md border border-[#272A34] bg-[#121418] flex-wrap">
              <input type="checkbox" data-testid={`venue-toggle-${v.id}`} checked={v.enabled !== false} onChange={(e) => toggleVenue(v, e.target.checked)} className="accent-amber-600 w-4 h-4 shrink-0" title={v.enabled !== false ? "Activée" : "Désactivée"} />
              <div className="flex-1 min-w-[160px]">
                <div className="font-serif text-sm text-stone-100 truncate">{v.name} <span className="text-stone-500 text-xs">· {v.city}{v.country && v.country !== "CH" ? ` (${v.country})` : ""}</span></div>
                <div className="flex flex-wrap items-center gap-1 my-0.5">
                  {v.type && <span data-testid={`venue-type-${v.id}`} className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-[#1c1f27] border border-[#2c3140] text-stone-300">{VENUE_TYPE_LABELS[v.type] || v.type}</span>}
                  {v.priority && <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${v.priority === "A" ? "bg-amber-900/50 text-amber-300 border border-amber-700/40" : v.priority === "C" ? "bg-stone-800 text-stone-400 border border-stone-700" : "bg-indigo-900/40 text-indigo-300 border border-indigo-700/40"}`}>Prio {v.priority}</span>}
                </div>
                <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1 text-[11px] font-mono truncate max-w-full"><ExternalLink className="w-3 h-3 shrink-0" /> {v.url}</a>
              </div>
              <span className="font-mono text-[10px] text-stone-500">{Number(v.lat).toFixed(3)}, {Number(v.lng).toFixed(3)}</span>
              <button data-testid={`venue-del-${v.id}`} onClick={() => delVenue(v)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
          <input data-testid="venue-new-name" value={venueForm.name} onChange={(e) => setVenueForm({ ...venueForm, name: e.target.value })} placeholder="Nom" className={`${inputCls} col-span-2`} />
          <input data-testid="venue-new-city" value={venueForm.city} onChange={(e) => setVenueForm({ ...venueForm, city: e.target.value })} placeholder="Ville" className={inputCls} />
          <input data-testid="venue-new-url" value={venueForm.url} onChange={(e) => setVenueForm({ ...venueForm, url: e.target.value })} placeholder="URL programme" className={`${inputCls} col-span-2 sm:col-span-1`} />
          <input data-testid="venue-new-lat" value={venueForm.lat} onChange={(e) => setVenueForm({ ...venueForm, lat: e.target.value })} placeholder="Lat" className={inputCls} />
          <input data-testid="venue-new-lng" value={venueForm.lng} onChange={(e) => setVenueForm({ ...venueForm, lng: e.target.value })} placeholder="Lng" className={inputCls} />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <select data-testid="venue-new-type" value={venueForm.type} onChange={(e) => setVenueForm({ ...venueForm, type: e.target.value })} className={inputCls}>
            <option value="salle">Salle</option>
            <option value="festival_specialise">Festival spécialisé</option>
            <option value="festival_generaliste">Festival généraliste</option>
            <option value="frontalier">Frontalier</option>
          </select>
          <select data-testid="venue-new-priority" value={venueForm.priority} onChange={(e) => setVenueForm({ ...venueForm, priority: e.target.value })} className={inputCls}>
            <option value="A">Priorité A</option>
            <option value="B">Priorité B</option>
            <option value="C">Priorité C</option>
          </select>
          <input data-testid="venue-new-country" value={venueForm.country} onChange={(e) => setVenueForm({ ...venueForm, country: e.target.value })} placeholder="Pays (CH)" className={inputCls} />
        </div>
        <button data-testid="venue-add" onClick={addVenue} className="mt-2 inline-flex items-center gap-1.5 bg-[#181A20] border border-[#272A34] hover:border-indigo-500/40 text-stone-200 px-3 py-2 rounded-md text-sm"><Plus className="w-4 h-4 text-indigo-300" /> Ajouter la salle</button>
      </div>

      {/* Concerts à valider */}
      <div className="mb-4 rounded-md border border-amber-800/40 bg-amber-950/10 p-3" data-testid="aw-queue-panel">
        <div className="flex items-center gap-2 mb-2">
          <CalendarClock className="w-4 h-4 text-amber-400" />
          <h4 className="font-serif text-sm text-amber-100">Concerts à valider ({queue.length})</h4>
        </div>
        {queue.length === 0 ? (
          <div className="text-xs text-stone-500" data-testid="aw-queue-empty">Aucun concert en attente de validation.</div>
        ) : (
          <div className="space-y-2">
            {queue.map((e) => (
              <div key={e.slug} data-testid={`aw-queue-${e.slug}`} className="rounded-md border border-[#272A34] bg-[#121418]">
                <div className="flex items-center gap-3 p-2.5 flex-wrap">
                  <div className="flex-1 min-w-[160px]">
                    <div className="font-serif text-sm text-stone-100 truncate">{e.title}</div>
                    <div className="text-[11px] font-mono text-stone-500 flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{e.city || "—"}{e.country ? `, ${e.country}` : ""}</span>
                      <span>· {(e.start_date || "").slice(0, 10)}</span>
                      {e.confidence_score != null && <span>· {e.confidence_score}/100</span>}
                      {e.source_name && <span>· {e.source_name}</span>}
                    </div>
                  </div>
                  {(e.official_url || e.ticket_url) && (
                    <a href={e.official_url || e.ticket_url} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1 text-[11px] font-mono"><ExternalLink className="w-3 h-3" /> lien</a>
                  )}
                  <button data-testid={`aw-queue-edit-${e.slug}`} onClick={() => (editSlug === e.slug ? setEditSlug(null) : startEdit(e))} className="inline-flex items-center gap-1 text-xs bg-[#181A20] border border-indigo-800/50 text-indigo-200 hover:bg-indigo-950/30 px-3 py-1.5 rounded-md"><Pencil className="w-3.5 h-3.5" /> Corriger</button>
                  <button data-testid={`aw-queue-publish-${e.slug}`} onClick={() => publishConcert(e.slug)} className="inline-flex items-center gap-1 text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded-md"><Check className="w-3.5 h-3.5" /> Publier</button>
                  <button data-testid={`aw-queue-reject-${e.slug}`} onClick={() => rejectConcert(e.slug)} className="inline-flex items-center gap-1 text-xs bg-[#181A20] border border-red-800/50 text-red-300 hover:bg-red-950/30 px-3 py-1.5 rounded-md"><X className="w-3.5 h-3.5" /> Rejeter</button>
                </div>
                {editSlug === e.slug && (
                  <div className="border-t border-[#272A34] p-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`aw-queue-edit-box-${e.slug}`}>
                    <input data-testid={`aw-edit-title-${e.slug}`} value={editForm.title} onChange={(ev) => setEditForm({ ...editForm, title: ev.target.value })} placeholder="Titre" className={inputCls} />
                    <input data-testid={`aw-edit-city-${e.slug}`} value={editForm.city} onChange={(ev) => setEditForm({ ...editForm, city: ev.target.value })} placeholder="Ville" className={inputCls} />
                    <input data-testid={`aw-edit-venue-${e.slug}`} value={editForm.venue} onChange={(ev) => setEditForm({ ...editForm, venue: ev.target.value })} placeholder="Lieu" className={inputCls} />
                    <input data-testid={`aw-edit-country-${e.slug}`} value={editForm.country} onChange={(ev) => setEditForm({ ...editForm, country: ev.target.value })} placeholder="Pays (CH)" className={inputCls} />
                    <input data-testid={`aw-edit-start-${e.slug}`} type="date" value={editForm.start_date} onChange={(ev) => setEditForm({ ...editForm, start_date: ev.target.value })} className={inputCls} />
                    <input data-testid={`aw-edit-end-${e.slug}`} type="date" value={editForm.end_date} onChange={(ev) => setEditForm({ ...editForm, end_date: ev.target.value })} className={inputCls} />
                    <input data-testid={`aw-edit-official-${e.slug}`} value={editForm.official_url} onChange={(ev) => setEditForm({ ...editForm, official_url: ev.target.value })} placeholder="Lien officiel" className={inputCls} />
                    <input data-testid={`aw-edit-ticket-${e.slug}`} value={editForm.ticket_url} onChange={(ev) => setEditForm({ ...editForm, ticket_url: ev.target.value })} placeholder="Billetterie" className={inputCls} />
                    <div className="sm:col-span-2 flex gap-2">
                      <button data-testid={`aw-edit-save-${e.slug}`} onClick={() => saveEdit(e.slug)} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-4 py-2 rounded-md text-sm"><Save className="w-4 h-4" /> Enregistrer (reste à valider)</button>
                      <button data-testid={`aw-edit-cancel-${e.slug}`} onClick={() => setEditSlug(null)} className="text-xs text-stone-400 hover:text-stone-200 px-3">Annuler</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {revisions.length > 0 && (
        <div className="mb-4 rounded-md border border-orange-800/40 bg-orange-950/10 p-3" data-testid="aw-revisions-panel">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-4 h-4 text-orange-400" />
            <h4 className="font-serif text-sm text-orange-100">Révisions proposées ({revisions.length})</h4>
          </div>
          <p className="text-[11px] text-stone-400 mb-2">Changements détectés sur des événements déjà publiés. La fiche publique n'est PAS modifiée tant que vous n'appliquez pas.</p>
          <div className="space-y-2">
            {revisions.map((rev) => (
              <div key={rev.id} data-testid={`aw-revision-${rev.id}`} className="flex items-start gap-3 p-2.5 rounded-md border border-[#272A34] bg-[#121418] flex-wrap">
                <div className="flex-1 min-w-[180px]">
                  <div className="font-serif text-sm text-stone-100">{rev.title}</div>
                  <div className="text-[11px] font-mono text-stone-400 mt-1 space-y-0.5">
                    {Object.entries(rev.changes || {}).map(([k, v]) => (
                      <div key={k}><span className="text-stone-500">{k}:</span> <span className="text-red-300 line-through">{String(rev.current?.[k] ?? "—").slice(0, 40)}</span> → <span className="text-emerald-300">{String(v).slice(0, 40)}</span></div>
                    ))}
                  </div>
                  {rev.source_name && <div className="text-[10px] font-mono text-stone-500 mt-1">source: {rev.source_name}</div>}
                </div>
                <button data-testid={`aw-revision-apply-${rev.id}`} onClick={() => applyRevision(rev.id)} className="inline-flex items-center gap-1 text-xs bg-emerald-700 hover:bg-emerald-600 text-white font-semibold px-3 py-1.5 rounded-md"><Check className="w-3.5 h-3.5" /> Appliquer</button>
                <button data-testid={`aw-revision-dismiss-${rev.id}`} onClick={() => dismissRevision(rev.id)} className="inline-flex items-center gap-1 text-xs bg-[#181A20] border border-red-800/50 text-red-300 hover:bg-red-950/30 px-3 py-1.5 rounded-md"><X className="w-3.5 h-3.5" /> Ignorer</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <WatchlistTourUrls />

      <div className="space-y-2 mb-4">
        {artists.length === 0 && <div className="text-sm text-stone-500" data-testid="aw-none">{t("aw_none")}</div>}
        {artists.map((a) => (
          <div key={a.id} data-testid={`aw-artist-${a.id}`} className="rounded-md border border-[#272A34] bg-[#181A20]">
            <div className="flex items-center gap-3 p-3 flex-wrap">
              <span className="font-serif text-stone-100 flex-1 min-w-[120px] truncate">{a.name}</span>
              <div className="flex items-center gap-2 text-[11px] font-mono">
                <a href={a.links?.bandsintown} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Bandsintown</a>
                <a href={a.links?.songkick} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Songkick</a>
                <a href={a.links?.google} target="_blank" rel="noopener noreferrer" className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Google</a>
              </div>
              {a.last_run && <span className="font-mono text-[11px] text-stone-500">{a.last_count} · {new Date(a.last_run).toLocaleDateString()}</span>}
              <button data-testid={`aw-paste-toggle-${a.id}`} onClick={() => { setOpenId(openId === a.id ? null : a.id); setPasteText(""); }} className="inline-flex items-center gap-1 text-xs bg-indigo-900/40 hover:bg-indigo-800/50 text-indigo-200 border border-indigo-700/50 px-2.5 py-1.5 rounded-md"><ClipboardPaste className="w-3.5 h-3.5" /> {t("aw_paste")}</button>
              <button data-testid={`aw-del-${a.id}`} onClick={() => del(a)} className="text-red-400/70 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
            {openId === a.id && (
              <div className="border-t border-[#272A34] p-3 space-y-2" data-testid={`aw-paste-box-${a.id}`}>
                <textarea data-testid={`aw-paste-text-${a.id}`} rows={5} value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder={t("aw_paste_ph")} className={inputCls} />
                <button data-testid={`aw-extract-${a.id}`} onClick={() => extract(a)} disabled={busy || !pasteText.trim()} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {t("aw_extract")}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <input data-testid="aw-new-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={t("aw_add_ph")} className={`${inputCls} flex-1 min-w-[220px]`} />
        <button data-testid="aw-add" onClick={add} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#181A20] border border-[#272A34] hover:border-indigo-500/40 text-stone-200 px-3 py-2 rounded-md text-sm"><Plus className="w-4 h-4 text-indigo-300" /> {t("aw_add")}</button>
      </div>
    </div>
  );
};

export default ArtistWatch;
